import React, { Fragment, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { TStyle, useClassnames, useBodyScrollLock, IconClear } from '@wb-tech/ui-react';
import { Button, IPropsButton, Text, Title } from '@wb-tech/ui-react/v2';
import { Transition, TransitionStatus } from 'react-transition-group';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import { useDispatch, useSelector } from '#core/store';

import { getMediaQuery } from '#component/media-query/slice';
import { sliderMenu, TOpenName } from '#component/slider/slice';

import style from './index.module.pcss';

export type TDirection = 'left' | 'right' | 'bottom' | 'top';

export interface IButtonProps {
    isShow: boolean,
    onClick: () => void,
    presetStyle?: IPropsButton['presetStyle'],
    text: string,
    disabled?: boolean

}

interface IProps {
    children: ReactNode,
    direction?: TDirection,
    contentPaddingPreset?: 'enable' | 'disable',
    name: TOpenName,
    className?: TStyle | string,
    title?: ReactNode,
    isInnerScroll?: boolean,
    additionButton?: IButtonProps,
    applyButton?: IButtonProps,
    resetButton?: IButtonProps,
    closeButton?: {
        onClick: () => void
    },
    isCloseIconShow?: boolean
}

export const Slider = ({ direction = 'bottom', contentPaddingPreset = 'enable', isInnerScroll = false, ...props }: IProps) => {
    const cn = useClassnames(style, props.className);
    const { t } = useTranslation();
    const { enableBodyScroll, disableBodyScroll } = useBodyScrollLock();

    const dispatch = useDispatch();

    const { menuOpened, isTablet } = useSelector((state) => ({
        menuOpened: state[sliderMenu.name].open,
        isTablet  : state[getMediaQuery.name].isTablet
    }), shallowEqual);

    const $root = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const tempRef = $root.current;

        if(tempRef && menuOpened) {
            disableBodyScroll(tempRef);
        }

        return () => {
            if(tempRef) {
                enableBodyScroll(tempRef);
            }
        };
    }, [disableBodyScroll, enableBodyScroll, menuOpened]);

    const handleClickClose = useCallback(() => {
        dispatch(sliderMenu.actions.reset());

        if(props?.closeButton) {
            props.closeButton?.onClick();
        }
    }, [dispatch, props?.closeButton]);

    const elAdditionButton = useMemo(() => {
        if(props?.additionButton?.isShow) {
            return (
                <button
                    type="button"
                    className={cn({
                        'slider__button_right'    : true,
                        'slider__sidebar-addition': true
                    })}
                    onClick={props.additionButton.onClick}
                >
                    <Text
                        presetSize="body"
                        presetColor="brand"
                    >
                        {props.additionButton.text}
                    </Text>
                </button>
            );
        }
    }, [props?.additionButton, cn]);

    const elContent = useCallback((state: TransitionStatus) => {
        const content = (
            <div
                ref={$root}
                className={cn('slider__wrapper', `slider__wrapper_${direction}`, `slider__wrapper_${contentPaddingPreset}`, {
                    'slider__wrapper_without-background': direction !== 'bottom',
                    'slider__wrapper_with-inner-scroll' : isInnerScroll,
                    [`slider__wrapper_${state}`]        : state
                })}
            >
                <div className={cn('slider__main-content')}>
                    {props.title && (
                        <div className={cn('slider__header')}>
                            <button
                                type="button"
                                className={cn({
                                    'slider__button_left'  : true,
                                    'slider__sidebar-close': true
                                })}
                                onClick={handleClickClose}
                            >
                                <Text
                                    presetSize="body"
                                    presetColor="brand"
                                >
                                    {t('components.slider.buttons.close')}
                                </Text>
                            </button>
                            <Title tagName="h4">{props.title}</Title>
                            {elAdditionButton}
                        </div>
                    )}
                    {props.isCloseIconShow && (
                        <Button
                            presetStyle="ghost"
                            className={cn('slider__close-icon')}
                            onClick={handleClickClose}
                        >
                            <IconClear />
                        </Button>
                    )}
                    {props.children}
                </div>
                {(props?.applyButton?.isShow || props?.resetButton?.isShow) && (
                    <div className={cn('slider__buttons')}>
                        {props?.applyButton?.isShow && (
                            <Button
                                presetStyle={props.applyButton.presetStyle}
                                disabled={props?.applyButton?.disabled}
                                onClick={props?.applyButton.onClick}
                            >
                                {props?.applyButton.text}
                            </Button>
                        )}
                        {props?.resetButton?.isShow && (
                            <Button
                                presetStyle={props.resetButton.presetStyle}
                                disabled={props?.resetButton?.disabled}
                                onClick={props?.resetButton.onClick}
                            >
                                {props?.resetButton.text}
                            </Button>
                        )}
                    </div>
                )}
            </div>
        );

        if(direction !== 'bottom') {
            return content;
        }

        return (
            <div
                ref={$root}
                className={cn('slider')}
            >
                {content}
            </div>
        );
    }, [
        cn,
        direction,
        contentPaddingPreset,
        isInnerScroll,
        props.title,
        props.children,
        props.applyButton,
        props.resetButton,
        props.isCloseIconShow,
        handleClickClose,
        t,
        elAdditionButton
    ]);

    return createPortal(
        <Transition
            nodeRef={$root}
            in={menuOpened === props.name}
            timeout={300}
            unmountOnExit={true}
            mountOnEnter={true}
        >
            {(state) => {
                return (
                    <Fragment>
                        {elContent(state)}
                        {isTablet && (
                            <button
                                aria-label="Close slider"
                                type="button"
                                onClick={handleClickClose}
                                className={cn('slider__overlay', {
                                    [`slider__overlay_${state}`]: state
                                })}
                            />
                        )}
                    </Fragment>
                );
            }}
        </Transition>,
        document.body
    );
};
